// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { ITelemetryItem } from '@microsoft/applicationinsights-web';

import { REQUEST_RESPONSE_EVENT_NAME, disableLocalhostTelemetry } from './constants';
import { camelCaseToTitleCase } from './../../helpers/caseManipulation';
import CorrelationVector from './../../lib/CorrelationVector';
import { Environment } from './../../environments';
import { allRoutes, IRoute } from './../../views';
import { msalInstance } from '../../../src/index';

const allPaths: string[] = allRoutes.map((route: IRoute) => route.path.toUpperCase());

export function transformUriToPageName(uri: string): string | undefined {
    // Default URI's like '/' or '{window.location.origin}/' to "Homepage"
    if (!uri || uri === `${window.location.origin}/` || uri === '/') {
        return 'Homepage';
    }

    const pageNameSubstring: string = uri.replace(window.location.origin, '');
    const foundRoute: boolean = allPaths.includes(pageNameSubstring.toUpperCase());

    // Return undefined if the url does not contain a valid page name
    if (!foundRoute) {
        return;
    }

    // Input should be like '/authorize' '/'
    // Strip out the hostname, and then remove the leading '/' before transforming each path into Title Case
    // Only return valid page name
    return pageNameSubstring
        .substring(pageNameSubstring.startsWith('/') ? 1 : 0)
        .split('/')
        .map((subPath: string) => camelCaseToTitleCase(subPath))
        .join(' > ');
}


function filterNonEmptyValues(properties: any) {
    return Object.entries(properties).reduce((acc: { [key: string]: any }, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            acc[key] = value;
        }
        return acc;
    }, {} as { [key: string]: any });
}


export function telemetryInitializer(item: ITelemetryItem): boolean | void {
    // Prevent localhost telemetry, can also use the disableTelemetry config value but it may help for debugging purposes that it actually enter this function
    if (disableLocalhostTelemetry && window.location.hostname.toUpperCase().includes('LOCALHOST')) {
        return false;
    }

    // All of the below logic is derived by guessing and checking/experimental trials.
    if (item.baseData && item.baseData.type && item.baseData.type.toUpperCase() === 'AJAX') {
        // Override operationId with a CV
        const originalId = item.baseData.id || item.tags && item.tags['ai.operation.id'];
        const operationId = item.baseData && item.baseData.properties && item.baseData.properties.correlationVector
            || CorrelationVector.getPreviousCV(); // Potential for duplicates for things like /geography 
        let loggedInUserInfo = sessionStorage.getItem("loggedInuserInfo");
        if(!loggedInUserInfo) {
             const accounts = msalInstance.getAllAccounts();
             loggedInUserInfo = accounts && accounts[0] && accounts[0].username;
        }
        if (item.tags) {
            // Parent id should already be set from 'appInsights.context.telemetryTrace.parentID = cv.substring(0, cv.indexOf('.'));'
            item.tags['ai.operation.id'] = operationId;
            
            //For better App Insights dashboard grouping of API calls, make generic versions of the API urls by getting rid of claim #s, serial #s, etc. in URL
            let operationName = item.baseData.name.replace(Environment.getEnvironment.hostname, '');
            operationName = operationName.replace(/(\?.*)/, '');
            operationName = operationName.replace(/(devices\/)([^\/]*)(\/?)/, '$1{id}$3');
            operationName = operationName.replace(/claimOrderBulk\/(.*)/, 'claimOrderBulk/{id}');
            operationName = operationName.replace(/(claimOrders\/)([^\/]*)(\/?)/, '$1{id}$3');
            operationName = operationName.replace(/lineItems\/(.*)\/events\/(.*)\/notifications/, 'lineItems/{id}/events/{id}/notifications');
            operationName = operationName.replace(/servicenotification\/(.*)\/cancel/, 'servicenotification/{id}/cancel');
            operationName = operationName.replace(/language\/(.*)\/faqTranslation/, 'language/{language}/faqTranslation');
            operationName = operationName.replace(/language\/(.*)\/lists/, 'language/{language}/lists');
                        
            item.tags['ai.operation.name'] = operationName;
        }

        item.baseData.id = operationId;
        item.baseData.properties = filterNonEmptyValues({
            ...item.baseData.properties,
            originalId,
            loggedInUserInfo
        });
        
        // Throw it inside the data object in case that's how custom properties need to be sent in future
        item.data = { ...item.data, ...item.baseData.properties };
    } else if (item.baseData && (item.baseType === 'EventData' || item.baseData.name === REQUEST_RESPONSE_EVENT_NAME)) {
        const operationId = item.baseData.properties.correlationVector;

        if (item.tags) {
            item.tags['ai.operation.id'] = operationId;

            if (item.baseData.name === REQUEST_RESPONSE_EVENT_NAME) {
                item.tags['ai.operation.name'] = item.baseData.properties.requestName;
            }
        }

    } else if ((item.baseType && item.baseType.toUpperCase() === 'EXCEPTIONDATA')
            || (item.baseType && item.baseType.toUpperCase() === 'MESSAGEDATA')
            || (item.baseData && item.baseData.exceptions)) {
        const operationId = item.baseData && item.baseData.properties && item.baseData.properties.correlationVector || CorrelationVector.getPreviousCV();
        const operationParentId = operationId.substring(0, operationId.indexOf('.'));
        const originalId = item.ext && item.ext.trace && item.ext.trace.traceID;
        if (item.ext) {
            if (item.ext.trace) {
                item.ext.trace.traceID = operationId;
                item.ext.trace.parentID = operationParentId;
            }
        }
        if (item.baseData) {
            item.baseData.properties = {
                ...item.baseData.properties,
                originalId,
            };
        }
        item.data = { ...item.data, ...(item.baseData && item.baseData.properties) };
    } else if (item && item.baseType && item.baseType.toUpperCase() === 'PAGEVIEWDATA') {
        if (item.baseData) {
            const pageName: string | undefined = transformUriToPageName(item.baseData.uri || window.location.pathname);
            
            // Don't log page if page name isn't found
            if (!pageName) {
                return false;
            }
            
            item.baseData.name = pageName;
        }
        
        const operationId = CorrelationVector.getCurrentValue();
        const operationParentId = operationId.substring(0, operationId.indexOf('.'));
        const originalId = item.ext && item.ext.trace && item.ext.trace.traceID;
        if (item.ext) {
            if (item.ext.trace) {
                item.ext.trace.traceID = operationId;
                item.ext.trace.parentID = operationParentId;
            }
        }
        if (item.baseData) {
            item.baseData.properties = {
                ...item.baseData.properties,
                originalId,
            };
        }
        item.data = { ...item.data, ...(item.baseData && item.baseData.properties) };
    }
    
    return true;
}

export default telemetryInitializer;
